import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { PostSection } from 'src/features/feeds/components/feeds/post-section'
import { useUserByUsername } from 'src/shared/features/users/api/accounts/use-user-by-username'
import { useUserPrivacyPreferences } from 'src/shared/features/users/api/preferences/use-user-privacy-preferences'
import UserProfileCard from 'src/shared/features/users/components/cards/user-profile-card'
import UserFeedList from 'src/shared/features/users/components/public-profile/feed/user-feed-list'
import ProfileOverview from 'src/shared/features/users/components/public-profile/overview/profile-overview'
import PortfolioList from 'src/shared/features/users/components/public-profile/portfolio/portfolio-list'
import ReviewSection from 'src/shared/features/users/components/public-profile/reviews/review-section'
import PublicProfileReviewsSection from 'src/shared/features/users/components/public-profile/section/reviews'
import PublicProfileTransactionsSection from 'src/shared/features/users/components/public-profile/section/transactions'
import PublicProfileSectionHeader from 'src/shared/features/users/components/public-profile/section-header'
import { usePublicProfileStore } from 'src/shared/features/users/components/public-profile/stores/use-public-profile-store'
import PublicUserProfileFooter from 'src/shared/features/users/components/public-profile/ui/footer'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'

const Tab = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => {
  return (
    <div
      className={cn(
        'flex flex-1 cursor-pointer items-center justify-center bg-white py-[18px] transition-all duration-200 ease-in-out xs:max-w-[127px] xs:py-5',
        active && 'border-b-primary',
      )}
      onClick={onClick}
      style={
        {
          position: 'relative',
          '--slide-progress': active ? '1' : '0',
        } as React.CSSProperties
      }
    >
      <p
        className={cn(
          'text-sm font-semibold leading-[1] text-muted transition-all duration-200 ease-in-out sm:text-base',
          active && 'font-bold text-primary',
        )}
      >
        {label}
      </p>
      {/* the underline for the tab regardless if active or not */}
      <div
        className="absolute bottom-[-1.5px] left-0 h-[1.5px] w-full rounded-full bg-[#efefef] transition-transform duration-300 ease-out"
        style={{
          transformOrigin: 'left',
        }}
      />
      {/* the underline for the tab if active */}
      <div
        className="absolute bottom-[-1.5px] left-0 h-[1.5px] w-full rounded-full bg-primary transition-transform duration-300 ease-out"
        style={{
          transform: `scaleX(${active ? 1 : 0})`,
          transformOrigin: 'left',
        }}
      />
    </div>
  )
}

function SectionDivider() {
  return <div className="h-px w-full bg-[#EFEFEF]" />
}

export const ProfileSectionsContainer = ({ userId, isPublic = false }: { userId: number; isPublic?: boolean }) => {
  const { activeTab, setIsPublicProfile } = usePublicProfileStore()
  const { data: preferences } = useUserPrivacyPreferences(userId)

  useEffect(() => {
    setIsPublicProfile(isPublic)
  }, [setIsPublicProfile, isPublic])

  const showActivity = preferences?.postsVisibleToConsumer

  return (
    <div className="flex w-full flex-col gap-4">
      <PublicProfileSectionHeader userId={userId} />
      {activeTab === 'activity' && showActivity && (
        <div className="-mt-8 w-full text-clip">
          <PostSection userId={userId} isPublic={isPublic} />
        </div>
      )}
      {activeTab === 'reviews' && (
        <div className="w-full text-clip">
          <PublicProfileReviewsSection userId={userId} />
        </div>
      )}
      {activeTab === 'transactions' && (
        <div className="w-full text-clip">
          <PublicProfileTransactionsSection userId={userId} />
        </div>
      )}
    </div>
  )
}

type DesktopActiveTab = 'overview' | 'portfolio' | 'reviews' | 'activity'

const PublicUserProfile = () => {
  const { username } = useParams<{ username: string }>()
  const { data: user } = useUserByUsername(username || '')
  const navigate = useNavigate()
  // const { activeTab } = useMobilePublicProfileStore()
  const { isMobile, isSmTabletOrSmaller } = useBreakpoint()

  const portfolioRef = useRef<HTMLDivElement>(null)
  const reviewsRef = useRef<HTMLDivElement>(null)
  const activityRef = useRef<HTMLDivElement>(null)
  const overviewRef = useRef<HTMLDivElement>(null)

  const [activeTabDesktop, setActiveTabDesktop] = useState<DesktopActiveTab>('overview')

  const tabScrollOffset = isMobile ? 91 : isSmTabletOrSmaller ? 95 : 110

  const scrollToTab = (tab: DesktopActiveTab) => {
    if (tab === 'overview' && overviewRef.current) {
      window.scrollTo({ top: overviewRef.current.offsetTop - tabScrollOffset, behavior: 'smooth' })
    }
    if (tab === 'portfolio' && portfolioRef.current) {
      window.scrollTo({ top: portfolioRef.current.offsetTop - tabScrollOffset, behavior: 'smooth' })
    }
    if (tab === 'reviews' && reviewsRef.current) {
      window.scrollTo({ top: reviewsRef.current.offsetTop - tabScrollOffset, behavior: 'smooth' })
    }
    if (tab === 'activity' && activityRef.current) {
      window.scrollTo({ top: activityRef.current.offsetTop - tabScrollOffset, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (overviewRef.current) {
        const overviewTop = overviewRef.current.offsetTop
        if (window.scrollY > overviewTop - tabScrollOffset - 2) {
          setActiveTabDesktop('overview')
        }
      }
      if (portfolioRef.current) {
        const portfolioTop = portfolioRef.current.offsetTop
        if (window.scrollY > portfolioTop - tabScrollOffset - 2) {
          setActiveTabDesktop('portfolio')
        }
      }
      if (reviewsRef.current) {
        const reviewsTop = reviewsRef.current.offsetTop
        if (window.scrollY > reviewsTop - tabScrollOffset - 2) {
          setActiveTabDesktop('reviews')
        }
      }
      if (activityRef.current) {
        const activityTop = activityRef.current.offsetTop
        if (window.scrollY > activityTop - tabScrollOffset - 2) {
          setActiveTabDesktop('activity')
        }
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [tabScrollOffset])

  if (!user) {
    navigate('/404')
    return null
  }

  const onSendInquiry = () => {
    navigate(paths.marketplace.instantMatch.userId.getHref(user.id.toString()))
  }

  // Only show footer if user is a mortgage loan officer or real estate agent
  const shouldShowFooter = user.profession === 'mortgage_loan_officer' || user.profession === 'real_estate_agent'

  return (
    <>
      <meta name="theme-color" content="#f8f7f5" />
      <div className="mx-auto flex max-w-[1100px] flex-col pb-[150px]">
        {/* User Profile Card */}
        <div className="mb-[20px]">
          <UserProfileCard
            user={user}
            isPublic={true}
            primaryButtonText="Send inquiry"
            primaryButtonOnClick={onSendInquiry}
            showPrimaryButton={shouldShowFooter}
            showPhoneButton={shouldShowFooter}
          />
        </div>

        {/* Sticky Tabs */}
        <div className="sticky top-0 z-10 mx-auto w-full max-w-[1000px] bg-white px-5 md:px-0">
          <div className="flex flex-row items-start border-b-[1.5px] border-[#EFEFEF]">
            <Tab label="Overview" active={activeTabDesktop === 'overview'} onClick={() => scrollToTab('overview')} />
            <Tab label="Portfolio" active={activeTabDesktop === 'portfolio'} onClick={() => scrollToTab('portfolio')} />
            <Tab label="Reviews" active={activeTabDesktop === 'reviews'} onClick={() => scrollToTab('reviews')} />
            <Tab label="Activity" active={activeTabDesktop === 'activity'} onClick={() => scrollToTab('activity')} />
          </div>
        </div>

        <div className="mx-auto mt-[30px] w-full max-w-[1000px] space-y-[40px] px-5 md:space-y-[60px] md:px-0">
          {/* Overview */}
          <div ref={overviewRef}>
            <ProfileOverview userId={user.id} />
          </div>
          <SectionDivider />
          {/* Portfolio */}
          <div ref={portfolioRef}>
            <PortfolioList
              userId={user.id}
              username={username as string}
              profession={user.profession}
              firstName={user.firstName}
            />
          </div>
          <SectionDivider />
          {/* Reviews */}
          <div ref={reviewsRef}>
            <ReviewSection userId={user.id} username={username as string} />
          </div>
          <SectionDivider />
          {/* Activity */}
          <div ref={activityRef} className="min-h-[calc(100vh-230px)] md:min-h-[calc(100vh-260px)]">
            <UserFeedList userId={user.id} firstName={user.firstName} />
          </div>
        </div>
      </div>
      {/* Footer */}
      {shouldShowFooter && (
        // <div className="fixed inset-x-5 bottom-5 z-20 flex justify-center">
        //   <div className="flex w-full flex-row items-center gap-[12px] rounded-full bg-white px-[11px] py-[10px] shadow-[0_0_30px_0_rgba(27,48,41,0.15)] xs:w-fit xs:gap-[15px] xs:p-[13px]">
        //     <Button
        //       variant="default"
        //       onClick={onSendInquiry}
        //       className="min-w-[300px]:text-[15px] h-[50px] w-full min-w-[100px] rounded-full bg-[#F8F7F5] p-0 text-[14px] font-semibold text-primary xs:h-[55px] xs:w-[200px] xs:text-[17px]"
        //     >
        //       Send inquiry
        //     </Button>
        //     <RequestCallButton
        //       text={`Request call`}
        //       showPhoneIcon={false}
        //       username={username as string}
        //       className="h-[50px] w-full rounded-full border-none px-2 py-0 xs:h-[55px] xs:w-[200px]"
        //       textClassName="text-[14px] min-w-[300px]:text-[15px] xs:text-[17px] md:text-[17px] font-semibold line-clamp-1 leading-[1.5] break-all"
        //     />
        //   </div>
        // </div>
        <PublicUserProfileFooter onSendInquiry={onSendInquiry} username={username as string} />
      )}
    </>
  )
}

export default PublicUserProfile
